"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Cookie, X, Settings, Check, Shield } from "lucide-react"
import Link from "next/link"

type CookiePreferences = {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  })

  useEffect(() => {
    // Check if consent was already given
    const consent = localStorage.getItem("cookie-consent")
    if (!consent) {
      // Delay showing the popup for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAcceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true }
    localStorage.setItem("cookie-consent", JSON.stringify(allAccepted))
    localStorage.setItem("cookie-consent-date", new Date().toISOString())
    setIsVisible(false)
  }

  const handleAcceptSelected = () => {
    localStorage.setItem("cookie-consent", JSON.stringify(preferences))
    localStorage.setItem("cookie-consent-date", new Date().toISOString())
    setIsVisible(false)
  }

  const handleRejectNonEssential = () => {
    const essentialOnly = { necessary: true, analytics: false, marketing: false }
    localStorage.setItem("cookie-consent", JSON.stringify(essentialOnly))
    localStorage.setItem("cookie-consent-date", new Date().toISOString())
    setIsVisible(false)
  }

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === "necessary") return // Cannot disable necessary cookies
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6"
        >
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-card/95 backdrop-blur-xl border border-border rounded-2xl shadow-2xl shadow-black/20 overflow-hidden">
              {/* Subtle gradient accent */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-primary/50 to-transparent" />
              
              <div className="p-6 sm:p-8">
                {!showPreferences ? (
                  // Main consent view
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center">
                        <Cookie className="w-6 h-6 text-accent-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-foreground">
                          We value your privacy
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                          We use cookies to enhance your browsing experience, analyze site traffic, 
                          and personalize content. You can choose which cookies you&apos;d like to accept.
                        </p>
                      </div>
                      <button
                        onClick={() => setIsVisible(false)}
                        className="flex-shrink-0 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                        aria-label="Close"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Quick info badges */}
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted/50 text-xs text-muted-foreground">
                        <Shield className="w-3.5 h-3.5" />
                        GDPR Compliant
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted/50 text-xs text-muted-foreground">
                        <Check className="w-3.5 h-3.5" />
                        No data sold
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                      <button
                        onClick={handleAcceptAll}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-accent-primary text-background font-medium text-sm transition-all hover:bg-accent-primary/90 focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:ring-offset-2 focus:ring-offset-card"
                      >
                        <Check className="w-4 h-4" />
                        Accept All
                      </button>
                      
                      <button
                        onClick={handleRejectNonEssential}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center px-6 py-3 rounded-xl border border-border bg-card text-foreground font-medium text-sm transition-all hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-border focus:ring-offset-2 focus:ring-offset-card"
                      >
                        Essential Only
                      </button>
                      
                      <button
                        onClick={() => setShowPreferences(true)}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-muted-foreground font-medium text-sm transition-all hover:text-foreground hover:bg-muted/30 focus:outline-none"
                      >
                        <Settings className="w-4 h-4" />
                        Customize
                      </button>
                    </div>

                    {/* Policy links */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <Link href="/privacy" className="hover:text-foreground transition-colors underline-offset-2 hover:underline">
                        Privacy Policy
                      </Link>
                      <Link href="/cookies" className="hover:text-foreground transition-colors underline-offset-2 hover:underline">
                        Cookie Policy
                      </Link>
                    </div>
                  </div>
                ) : (
                  // Preferences view
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setShowPreferences(false)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                        aria-label="Back"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">
                          Cookie Preferences
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Customize your cookie settings
                        </p>
                      </div>
                    </div>

                    {/* Cookie categories */}
                    <div className="space-y-4">
                      {/* Necessary */}
                      <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-muted/30 border border-border/50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-foreground">Necessary</h4>
                            <span className="px-2 py-0.5 rounded text-xs bg-accent-primary/10 text-accent-primary">Required</span>
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Essential for the website to function. Cannot be disabled.
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-12 h-7 rounded-full bg-accent-primary/20 flex items-center justify-end px-1">
                            <div className="w-5 h-5 rounded-full bg-accent-primary" />
                          </div>
                        </div>
                      </div>

                      {/* Analytics */}
                      <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-muted/30 border border-border/50">
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground">Analytics</h4>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Help us understand how visitors interact with our website.
                          </p>
                        </div>
                        <button
                          onClick={() => togglePreference("analytics")}
                          className="flex-shrink-0"
                          aria-label={preferences.analytics ? "Disable analytics cookies" : "Enable analytics cookies"}
                        >
                          <div className={`w-12 h-7 rounded-full flex items-center px-1 transition-colors ${
                            preferences.analytics ? "bg-accent-primary/20 justify-end" : "bg-muted justify-start"
                          }`}>
                            <div className={`w-5 h-5 rounded-full transition-colors ${
                              preferences.analytics ? "bg-accent-primary" : "bg-muted-foreground/40"
                            }`} />
                          </div>
                        </button>
                      </div>

                      {/* Marketing */}
                      <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-muted/30 border border-border/50">
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground">Marketing</h4>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Used to deliver personalized advertisements and track their effectiveness.
                          </p>
                        </div>
                        <button
                          onClick={() => togglePreference("marketing")}
                          className="flex-shrink-0"
                          aria-label={preferences.marketing ? "Disable marketing cookies" : "Enable marketing cookies"}
                        >
                          <div className={`w-12 h-7 rounded-full flex items-center px-1 transition-colors ${
                            preferences.marketing ? "bg-accent-primary/20 justify-end" : "bg-muted justify-start"
                          }`}>
                            <div className={`w-5 h-5 rounded-full transition-colors ${
                              preferences.marketing ? "bg-accent-primary" : "bg-muted-foreground/40"
                            }`} />
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Save button */}
                    <div className="flex justify-end">
                      <button
                        onClick={handleAcceptSelected}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-accent-primary text-background font-medium text-sm transition-all hover:bg-accent-primary/90 focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:ring-offset-2 focus:ring-offset-card"
                      >
                        <Check className="w-4 h-4" />
                        Save Preferences
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
